/**
 * EditReminderModal — PATCH `/api/calendar/events/{id}` for manual
 * reminders. Same shape as `AddReminderModal` but pre-populates from
 * the passed-in event and sends only changed fields (so the backend's
 * `no_fields_to_update` guardrail never fires from a spurious submit).
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/api/client";

const REMINDER_OPTIONS = [
  { value: 0, label: "On the day" },
  { value: 1, label: "1 day before" },
  { value: 3, label: "3 days before" },
  { value: 7, label: "1 week before" },
  { value: 14, label: "2 weeks before" },
  { value: 30, label: "1 month before" },
];

function toDateInputValue(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

export default function EditReminderModal({ event, onOpenChange, onUpdated }) {
  const open = !!event;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [reminderDays, setReminderDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (event) {
      setTitle(event.title || "");
      setDescription(event.description || "");
      setEventDate(toDateInputValue(event.event_date));
      setReminderDays(event.reminder_days_before ?? 7);
      setError(null);
      setSubmitting(false);
    }
  }, [event]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!event) return;
    setSubmitting(true);
    setError(null);
    // Diff — only send changed fields.
    const patch = {};
    if (title.trim() !== (event.title || "")) patch.title = title.trim();
    if ((description || "") !== (event.description || "")) {
      patch.description = description.trim() || null;
    }
    const newIso = eventDate ? new Date(eventDate).toISOString() : null;
    const oldIso = new Date(event.event_date).toISOString();
    if (newIso && newIso !== oldIso) patch.event_date = newIso;
    if (Number(reminderDays) !== event.reminder_days_before) {
      patch.reminder_days_before = Number(reminderDays);
    }
    if (Object.keys(patch).length === 0) {
      setError("Nothing changed. Update a field or cancel.");
      setSubmitting(false);
      return;
    }
    try {
      const res = await api.patch(`/calendar/events/${event.id}`, patch);
      onUpdated?.(res.data);
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="calendar-edit-reminder-modal"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Edit reminder</DialogTitle>
          <DialogDescription>
            Update the title, notes, date, or lead time.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="edit-reminder-title"
              className="text-[11px] uppercase tracking-[0.16em] text-v2-textMuted"
            >
              Title
            </label>
            <input
              id="edit-reminder-title"
              data-testid="calendar-edit-reminder-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent"
              style={{
                background: "var(--v2-surface)",
                border: "1px solid var(--v2-border)",
              }}
            />
          </div>
          <div>
            <label
              htmlFor="edit-reminder-description"
              className="text-[11px] uppercase tracking-[0.16em] text-v2-textMuted"
            >
              Notes
            </label>
            <textarea
              id="edit-reminder-description"
              data-testid="calendar-edit-reminder-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              rows={3}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent resize-none"
              style={{
                background: "var(--v2-surface)",
                border: "1px solid var(--v2-border)",
              }}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="edit-reminder-date"
                className="text-[11px] uppercase tracking-[0.16em] text-v2-textMuted"
              >
                Date + time
              </label>
              <input
                id="edit-reminder-date"
                data-testid="calendar-edit-reminder-date-input"
                type="datetime-local"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent"
                style={{
                  background: "var(--v2-surface)",
                  border: "1px solid var(--v2-border)",
                }}
              />
            </div>
            <div>
              <label
                htmlFor="edit-reminder-lead"
                className="text-[11px] uppercase tracking-[0.16em] text-v2-textMuted"
              >
                Remind me
              </label>
              <select
                id="edit-reminder-lead"
                data-testid="calendar-edit-reminder-lead-select"
                value={reminderDays}
                onChange={(e) => setReminderDays(e.target.value)}
                className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent"
                style={{
                  background: "var(--v2-surface)",
                  border: "1px solid var(--v2-border)",
                }}
              >
                {REMINDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {error && (
            <p
              data-testid="calendar-edit-reminder-error"
              role="alert"
              className="text-xs text-red-400 leading-relaxed"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              data-testid="calendar-edit-reminder-cancel"
              className="text-sm text-v2-textMuted hover:text-v2-text px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !title.trim()}
              data-testid="calendar-edit-reminder-submit"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "rgba(167,139,250,0.9)" }}
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save changes
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
